import { useState, useEffect } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import styled from 'styled-components';
import CheckIcon from '@material-ui/icons/Check';
import CircularProgress from '@material-ui/core/CircularProgress';
import isURL from 'validator/lib/isURL';
import Text from 'components/base/Text';
import Input from 'components/base/Input';
import { Flex, Padding } from 'components/base/layout-components';
import { SPACES } from 'common/constants/sizes';
import axios from 'common/requester';
import { API } from 'common/constants/api-urls';
import { GIT_PROVIDERS } from 'common/constants/constants';
import githubLogo from 'images/logos/github-logo.svg';
import gitlabLogo from 'images/logos/gitlab-logo.svg';
import bitbucketLogo from 'images/logos/bitbucket-logo.svg';
import { getColor } from 'common/constants/_colors';
import { LBButton } from '@linearb/buzz';

const CONNECTED_SUCCESSFULLY_RESULT = 'connected_successfully';

const ProviderLogo = styled.img`
  padding-right: 1.5rem;
`;

const OrderedList = styled.ol`
  margin: unset;
  padding-left: 1.2rem;
  line-height: ${SPACES.medium};
  & > li {
    padding-bottom: 2rem;
  }
  & > li.scopes {
    padding-bottom: 1.2rem;
  }
`;

const InputWrapper = styled(Flex)`
  flex-grow: 1;
  display: flex;
  & > div {
    flex-grow: 1;
    padding-right: 1rem;
    width: 100%;
    display: flex;
  }
`;

const Footer = styled(Flex)`
  border-top: 1px solid ${getColor('catskillWhite')};
`;

const getProviderConfig = providerName => {
  const providersConfig = {
    [GIT_PROVIDERS.GITHUB]: {
      name: 'GitHub Enterprise Server',
      logo: githubLogo,
      exampleUrl: 'https://github.yourdomain.com',
      accessTestUrl: `${API.TEST_ACCESS_CONNECTIVITY_PATH}/github`
    },
    [GIT_PROVIDERS.GITLAB]: {
      name: 'Gitlab Server',
      logo: gitlabLogo,
      exampleUrl: 'https://gitlab.yourdomain.com',
      accessTestUrl: `${API.TEST_ACCESS_CONNECTIVITY_PATH}/gitlab`
    },
    [GIT_PROVIDERS.BITBUCKET_SERVER]: {
      name: 'Bitbucket Server',
      logo: bitbucketLogo,
      exampleUrl: 'https://bitbucket.yourdomain.com',
      accessTestUrl: `${API.TEST_ACCESS_CONNECTIVITY_PATH}/bitbucket_server`
    }
  };

  return GIT_PROVIDERS[providerName.toUpperCase()]
    ? providersConfig[GIT_PROVIDERS[providerName.toUpperCase()]]
    : {};
};

const HostedGitProviderModal = ({
  provider,
  onSubmit,
  onBack,
  configuration
}) => {
  const [baseUrl, setBaseUrl] = useState('');
  const [accessTestSuccessfully, setAccessTestSuccessfully] = useState(false);
  const [accessTestFailed, setAccessTestFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const testAccessConnectivity = async () => {
    const { accessTestUrl } = getProviderConfig(provider);
    const body = {
      self_hosted_uri: baseUrl
    };

    setAccessTestFailed(false);
    setLoading(true);

    const { result } = await axios.post(accessTestUrl, body);

    if (result === CONNECTED_SUCCESSFULLY_RESULT) {
      setTimeout(() => {
        setAccessTestSuccessfully(true);
        setLoading(false);
      }, 2000);
    } else {
      setAccessTestSuccessfully(false);
      setAccessTestFailed(true);
      setLoading(false);
    }
  };

  const debouncedAccessTestApi = useDebounceCallback(
    testAccessConnectivity,
    1000
  );

  useEffect(() => {
    if (isURL(baseUrl)) {
      debouncedAccessTestApi();
    }
  }, [baseUrl, debouncedAccessTestApi]);

  const { nat_gateway_ips } = configuration;
  const { name, logo, exampleUrl } = getProviderConfig(provider);

  return (
    <Flex direction='column' height='100%'>
      <Flex direction='column' pt='x-x-large' pl='big' pr='big' height='100%'>
        <Flex items='center'>
          <ProviderLogo src={logo} alt={name} />
          <Text size='x-x-x-large' weight='300' color='black'>
            Check your {name} connectivity
          </Text>
        </Flex>
        <Flex direction='row' pt='x-large'>
          <Text size='medium'>
            <OrderedList>
              <li>
                In order to connect successfully to your {name}, make sure your
                Firewall allows communications in/out to the following IP
                addresses: <b>{nat_gateway_ips}</b>
              </li>
              <li>
                Enter your {name} URL:
                <br />
                <Flex direction='row' grow='1' items='center'>
                  <Flex basis='50%' direction='column'>
                    <InputWrapper>
                      <Input
                        dataCy='baseUrlInput'
                        height='3rem'
                        type='url'
                        name='base-url'
                        width='36rem'
                        placeholder={`i.e. ${exampleUrl}`}
                        onChange={setBaseUrl}
                      />
                    </InputWrapper>
                  </Flex>
                  <Flex basis='50%'>
                    {loading && <CircularProgress size={26} />}
                    {!loading && accessTestSuccessfully && (
                      <CheckIcon
                        style={{
                          color: 'green',
                          width: '3.5rem',
                          height: '3.5rem'
                        }}
                      />
                    )}
                  </Flex>
                </Flex>
              </li>
            </OrderedList>

            <Padding l='small'>
              <LBButton
                data-cy='baseUrlInput-continue'
                disabled={!accessTestSuccessfully}
                size='lg'
                onClick={() => onSubmit({ baseUrl })}
              >
                Continue
              </LBButton>
              {accessTestFailed && (
                <Text
                  pt={SPACES.medium}
                  size='medium'
                  lineHeight='medium'
                  color='torchRed'
                >
                  We can't connect to your environment, please make sure the URL
                  is correct and the IP mentioned above is whitelisted
                </Text>
              )}
            </Padding>
          </Text>
        </Flex>
      </Flex>
      {
        <Footer padding='normal'>
          <LBButton variant='ghost' onClick={onBack}>
            &#60; Connect to a different Git provider
          </LBButton>
        </Footer>
      }
    </Flex>
  );
};

export default HostedGitProviderModal;
